import { useState, useEffect, useRef } from "react";
import { EALogoTree, EALogo2 } from "../../../assets";
import Button from "../../general/ui-blocks/elements/button";

function getApplyLabel() {
    try {
        const draft = JSON.parse(localStorage.getItem("extern_apply_draft") ?? "{}");
        return draft.submitted ? "Review Application" : "Claim Your Spot";
    } catch {
        return "Claim Your Spot";
    }
}

const PROGRAMS = [
    { label: "Admissions Overview", sub: null, href: "/admissions" },
    { label: "2027 Startup Sprint", sub: "14 days · Jan 2027", href: "/admissions/2027-startup-sprint" },
    { label: "Founder Track", sub: "28 days · Feb 2027", href: "/admissions/founder-track" },
    { label: "The Residency", sub: "48 days · May 2027", href: "/admissions/residency" },
];

export default function NavigationBar({ transparent = false }) {
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [atTop, setAtTop] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [admissionsOpen, setAdmissionsOpen] = useState(false);
    const [applyLabel, setApplyLabel] = useState(getApplyLabel);
    const navbarRef = useRef(null);

    useEffect(() => {
        const sync = () => setApplyLabel(getApplyLabel());
        window.addEventListener("storage", sync);
        return () => window.removeEventListener("storage", sync);
    }, []);

    // Hide/show on scroll with 10px up threshold
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const scrollDifference = lastScrollY - currentScrollY;

            setAtTop(currentScrollY < 80);

            if (currentScrollY > lastScrollY && currentScrollY > 10) {
                setIsVisible(false);
            } else if (scrollDifference > 10 || currentScrollY === 0) {
                setIsVisible(true);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [lastScrollY]);

    // Body padding for fixed navbar — skipped in transparent mode so the hero extends to y:0
    useEffect(() => {
        if (transparent) {
            document.body.style.paddingTop = "0";
            return () => { document.body.style.paddingTop = ""; };
        }
        const updateBodyPadding = () => {
            if (navbarRef.current) {
                document.body.style.paddingTop = `${navbarRef.current.offsetHeight}px`;
            }
        };
        updateBodyPadding();
        window.addEventListener("resize", updateBodyPadding);
        return () => {
            document.body.style.paddingTop = "";
            window.removeEventListener("resize", updateBodyPadding);
        };
    }, [transparent]);

    // Lock body scroll when drawer is open
    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [drawerOpen]);

    const closeDrawer = () => setDrawerOpen(false);
    const isTransparent = transparent && atTop;

    return (
        <>
            <header
                ref={navbarRef}
                className={`
                    fixed top-0 left-0 right-0 z-50
                    transition-transform duration-300 ease-in-out
                    ${isVisible ? "translate-y-0" : "-translate-y-full"}
                    py-2 py-lg-0
                `}
                style={{
                    backgroundColor: isTransparent ? "transparent" : "#e5e7eb",
                    transition: "transform 0.3s ease, background-color 0.4s ease",
                }}
            >
                <div className="container">
                    <nav className="navbar align-items-lg-end px-0 py-1 py-lg-3 navbar-light">
                        <div className="d-flex align-items-center w-100 gap-3">

                            {/* Logo */}
                            <div className="col-auto col-lg-4 d-flex align-items-center">
                                <a className="d-flex align-items-center gap-2" href="/">
                                    <img src={EALogoTree} alt="Extern Academy tree logo" className="h-20" />
                                    <img src={EALogo2} alt="Extern Academy logo" className="h-20" />
                                </a>
                            </div>

                            <div className="col">
                                <div className="d-flex align-items-center justify-content-lg-end gap-2 gap-lg-0">

                                    {/* Desktop nav */}
                                    <nav role="navigation" className="d-none d-lg-flex align-items-center">
                                        <ul className="nav navbar utility-nav fw-medium mb-0 align-items-center">

                                            {/* Admissions dropdown */}
                                            <li
                                                className="nav-item d-flex align-items-center position-relative"
                                                onMouseEnter={() => setAdmissionsOpen(true)}
                                                onMouseLeave={() => setAdmissionsOpen(false)}
                                            >
                                                <button
                                                    className="nav-link d-flex align-items-center gap-1"
                                                    style={{ background: "none", border: "none", cursor: "pointer", padding: "0.5rem 1rem" }}
                                                >
                                                    ADMISSIONS
                                                    <ChevronIcon open={admissionsOpen} />
                                                </button>

                                                {/* Dropdown panel */}
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "100%",
                                                        left: 0,
                                                        paddingTop: "8px",
                                                        minWidth: "240px",
                                                        zIndex: 100,
                                                        opacity: admissionsOpen ? 1 : 0,
                                                        pointerEvents: admissionsOpen ? "auto" : "none",
                                                        transform: admissionsOpen ? "translateY(0)" : "translateY(-6px)",
                                                        transition: "opacity 0.18s ease, transform 0.18s ease",
                                                    }}
                                                >
                                                    <div style={{ background: "#fff", borderRadius: "10px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", overflow: "hidden" }}>
                                                        {PROGRAMS.map(({ label, sub, href }, i) => (
                                                            <a
                                                                key={href}
                                                                href={href}
                                                                style={{
                                                                    display: "block",
                                                                    padding: "12px 18px",
                                                                    textDecoration: "none",
                                                                    borderBottom: i < PROGRAMS.length - 1 ? "1px solid #f3f4f6" : "none",
                                                                    transition: "background 0.12s",
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                                                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                                            >
                                                                <p style={{ margin: 0, fontSize: "0.875rem", fontWeight: 600, color: "#111827" }}>{label}</p>
                                                                {sub && <p style={{ margin: 0, fontSize: "0.75rem", color: "#9ca3af", marginTop: "2px" }}>{sub}</p>}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            </li>

                                            <li className="nav-item d-flex align-items-center">
                                                <a href="/stories/student-need-to-know" className="nav-link">FOR STUDENTS</a>
                                            </li>
                                            <li className="nav-item d-flex align-items-center">
                                                <a href="/stories/parent-need-to-know" className="nav-link">FOR PARENTS</a>
                                            </li>
                                            <li className="ms-3">
                                                <Button text={applyLabel.toUpperCase()} ctaURL="/apply" variant="btn-danger" />
                                            </li>
                                        </ul>
                                    </nav>

                                    {/* Mobile hamburger */}
                                    <button
                                        className="d-flex d-lg-none align-items-center justify-content-center ms-auto"
                                        onClick={() => setDrawerOpen(true)}
                                        aria-label="Open menu"
                                        style={{ background: "none", border: "none", cursor: "pointer", padding: "8px" }}
                                    >
                                        <HamburgerIcon />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Backdrop */}
            <div
                onClick={closeDrawer}
                style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.45)",
                    zIndex: 998,
                    opacity: drawerOpen ? 1 : 0,
                    pointerEvents: drawerOpen ? "auto" : "none",
                    transition: "opacity 0.3s ease",
                }}
            />

            {/* Right drawer */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: "min(320px, 85vw)",
                    background: "#fff",
                    zIndex: 999,
                    transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
                    transition: "transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
                    overflowY: "auto",
                }}
                aria-hidden={!drawerOpen}
            >
                {/* Drawer header */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "20px 24px",
                    borderBottom: "1px solid #f0f0f0",
                }}>
                    <a href="/" onClick={closeDrawer} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={EALogoTree} alt="Extern Academy tree logo" style={{ height: 48 }} />
                        <img src={EALogo2} alt="Extern Academy logo" style={{ height: 48 }} />
                    </a>
                    <button
                        onClick={closeDrawer}
                        aria-label="Close menu"
                        style={{ background: "none", border: "none", cursor: "pointer", padding: "8px", color: "#374151" }}
                    >
                        <CloseIcon />
                    </button>
                </div>

                {/* Drawer links */}
                <nav style={{ flex: 1, padding: "24px" }}>
                    <DrawerSection label="Admissions">
                        <DrawerLink href="/admissions" onClick={closeDrawer}>Overview</DrawerLink>
                        <DrawerLink href="/admissions/2027-startup-sprint" onClick={closeDrawer}>2027 Startup Sprint</DrawerLink>
                        <DrawerLink href="/admissions/founder-track" onClick={closeDrawer}>Founder Track</DrawerLink>
                        <DrawerLink href="/admissions/residency" onClick={closeDrawer}>The Residency</DrawerLink>
                    </DrawerSection>

                    <DrawerSection label="More">
                        <DrawerLink href="/stories/student-need-to-know" onClick={closeDrawer}>For Students</DrawerLink>
                        <DrawerLink href="/stories/parent-need-to-know" onClick={closeDrawer}>For Parents</DrawerLink>
                        <DrawerLink href="/financial-aid" onClick={closeDrawer}>Financial Aid</DrawerLink>
                        <DrawerLink href="/partners" onClick={closeDrawer}>Partners</DrawerLink>
                        <DrawerLink href="/contact-us" onClick={closeDrawer}>Contact</DrawerLink>
                    </DrawerSection>
                </nav>

                {/* Drawer CTA */}
                <div style={{ padding: "24px", borderTop: "1px solid #f0f0f0" }}>
                    <a
                        href="/apply"
                        onClick={closeDrawer}
                        style={{
                            display: "block",
                            width: "100%",
                            padding: "14px",
                            background: "#D23C1E",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            letterSpacing: "0.08em",
                            textAlign: "center",
                            textDecoration: "none",
                            borderRadius: "6px",
                        }}
                    >
                        {applyLabel.toUpperCase()} →
                    </a>
                </div>
            </div>
        </>
    );
}

function ChevronIcon({ open }) {
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}

function DrawerSection({ label, children }) {
    return (
        <div style={{ marginBottom: "28px" }}>
            <p style={{
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#9ca3af",
                marginBottom: "12px",
            }}>
                {label}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                {children}
            </div>
        </div>
    );
}

function DrawerLink({ href, onClick, children }) {
    return (
        <a
            href={href}
            onClick={onClick}
            style={{
                display: "block",
                padding: "10px 12px",
                fontSize: "1rem",
                fontWeight: 500,
                color: "#111827",
                textDecoration: "none",
                borderRadius: "6px",
                transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
            {children}
        </a>
    );
}

function HamburgerIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}
